import { useEffect, useMemo, useState } from "react";
import { Plus, Trash2, Pencil, Check, X, FolderTree, Search, ChevronRight, ChevronDown, Package, AlertTriangle } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { useAuth } from "@/context/AuthContext";

export default function Categories() {
  const { user } = useAuth();
  const editable = ["owner", "manager"].includes(user.role);
  const [cats, setCats] = useState([]);
  const [products, setProducts] = useState([]);
  const [name, setName] = useState("");
  const [q, setQ] = useState("");
  const [saving, setSaving] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editName, setEditName] = useState("");
  const [expanded, setExpanded] = useState({});
  const [confirmAll, setConfirmAll] = useState(false);
  const [deletingAll, setDeletingAll] = useState(false);

  const load = async () => {
    const [c, p] = await Promise.all([
      api.get("/categories"),
      api.get("/products?include_archived=true&limit=10000"),
    ]);
    setCats(c.data);
    setProducts(p.data);
  };
  useEffect(() => { load(); }, []);

  const byCategory = useMemo(() => {
    const m = {};
    for (const pr of products) {
      const k = pr.category || "Other";
      (m[k] = m[k] || []).push(pr);
    }
    for (const k of Object.keys(m)) m[k].sort((a, b) => (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: "base" }));
    return m;
  }, [products]);

  const add = async () => {
    const n = name.trim();
    if (!n) { toast.error("Category name is required"); return; }
    setSaving(true);
    try { await api.post("/categories", { name: n }); toast.success("Category added"); setName(""); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Could not add category"); }
    finally { setSaving(false); }
  };

  const startEdit = (c) => { setEditId(c.id); setEditName(c.name); };
  const cancelEdit = () => { setEditId(null); setEditName(""); };
  const saveEdit = async (c) => {
    const n = editName.trim();
    if (!n) { toast.error("Category name is required"); return; }
    if (n === c.name) { cancelEdit(); return; }
    try { await api.put(`/categories/${c.id}`, { name: n }); toast.success("Category updated"); cancelEdit(); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Could not update category"); }
  };

  const del = async (c) => {
    if (!window.confirm(`Delete category "${c.name}"? Existing products keep their label; this only removes it from the list.`)) return;
    try { await api.delete(`/categories/${c.id}`); toast.success("Category deleted"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const deleteAll = async () => {
    setDeletingAll(true);
    try {
      const { data } = await api.delete("/categories");
      toast.success(`Deleted ${data.deleted} categor${data.deleted === 1 ? "y" : "ies"}. Products kept.`);
      setConfirmAll(false); setExpanded({}); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Could not delete categories"); }
    finally { setDeletingAll(false); }
  };

  const toggle = (id) => setExpanded((p) => ({ ...p, [id]: !p[id] }));
  const filtered = cats.filter((c) => c.name.toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="h-full overflow-y-auto p-6" data-testid="categories-page">
      <PageHeader title="Categories" subtitle={`${cats.length} categories`}>
        <div className="flex items-center gap-2">
          <div className="relative"><Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-400" /><input data-testid="cat-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…" className="h-11 pl-11 pr-4 rounded-xl border border-zinc-200 bg-white w-56 focus:outline-none focus:ring-2 focus:ring-brand/30" /></div>
          {editable && cats.length > 0 && (
            <button onClick={() => setConfirmAll(true)} data-testid="delete-all-categories-btn" className="h-11 px-4 rounded-xl border border-red-200 text-red-600 font-semibold text-sm flex items-center gap-2 active:scale-95 hover:bg-red-50 whitespace-nowrap"><Trash2 size={16} />Delete All</button>
          )}
        </div>
      </PageHeader>

      {editable && (
        <div className="flex gap-2 mb-4 max-w-lg">
          <input data-testid="cat-name-input" value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} placeholder="New category name" className="flex-1 h-11 px-4 rounded-xl border border-zinc-200 bg-white focus:outline-none focus:ring-2 focus:ring-brand/30" />
          <button onClick={add} disabled={saving} data-testid="add-category-btn" className="h-11 px-5 rounded-xl bg-brand text-white font-semibold flex items-center gap-2 active:scale-95 disabled:opacity-50"><Plus size={18} />Add Category</button>
        </div>
      )}

      <div className="bg-white border border-zinc-200/70 rounded-2xl overflow-hidden" data-testid="cat-list">
        {filtered.length === 0 && (
          <div className="p-14 text-center text-zinc-400 flex flex-col items-center gap-2" data-testid="cat-empty-state">
            <FolderTree size={34} className="text-zinc-300" />
            <p className="font-semibold text-zinc-600">No categories yet</p>
            <p className="text-sm">Create your first category to organize your products.</p>
          </div>
        )}
        {filtered.map((c) => {
          const items = byCategory[c.name] || [];
          const isOpen = !!expanded[c.id];
          const isEditing = editId === c.id;
          return (
            <div key={c.id} data-testid={`cat-row-${c.id}`} className="border-b border-zinc-100 last:border-0">
              <div className="flex items-center justify-between px-5 py-3.5 hover:bg-zinc-50/60">
                <button type="button" onClick={() => !isEditing && toggle(c.id)} data-testid={`cat-expand-${c.id}`} className="flex items-center gap-3 min-w-0 flex-1 text-left" aria-expanded={isOpen}>
                  <span className="text-zinc-400 shrink-0">{isOpen ? <ChevronDown size={18} /> : <ChevronRight size={18} />}</span>
                  <span className="h-9 w-9 rounded-lg bg-brand-soft text-brand flex items-center justify-center shrink-0"><FolderTree size={17} /></span>
                  {isEditing ? (
                    <input autoFocus data-testid={`cat-edit-input-${c.id}`} value={editName} onClick={(e) => e.stopPropagation()} onChange={(e) => setEditName(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") saveEdit(c); if (e.key === "Escape") cancelEdit(); }} className="flex-1 h-9 px-3 rounded-lg border border-brand/40 bg-white text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-brand/30" />
                  ) : (
                    <span className="min-w-0">
                      <span className="block font-semibold truncate" data-testid={`cat-name-${c.id}`}>{c.name}</span>
                      <span className="block text-xs text-zinc-400" data-testid={`cat-count-${c.id}`}>{items.length} product{items.length === 1 ? "" : "s"}</span>
                    </span>
                  )}
                </button>
                {editable && (
                  <div className="flex items-center gap-1.5 shrink-0">
                    {isEditing ? (
                      <>
                        <button onClick={() => saveEdit(c)} data-testid={`cat-edit-save-${c.id}`} title="Save" className="h-9 w-9 rounded-lg border border-emerald-200 text-emerald-600 flex items-center justify-center active:scale-90"><Check size={16} /></button>
                        <button onClick={cancelEdit} data-testid={`cat-edit-cancel-${c.id}`} title="Cancel" className="h-9 w-9 rounded-lg border border-zinc-200 text-zinc-500 flex items-center justify-center active:scale-90"><X size={16} /></button>
                      </>
                    ) : (
                      <>
                        <button onClick={() => startEdit(c)} data-testid={`cat-edit-${c.id}`} title="Edit category" className="h-9 w-9 rounded-lg border border-zinc-200 text-zinc-600 flex items-center justify-center active:scale-90"><Pencil size={15} /></button>
                        <button onClick={() => del(c)} data-testid={`cat-delete-${c.id}`} title="Delete category" className="h-9 w-9 rounded-lg border border-red-200 text-red-600 flex items-center justify-center active:scale-90"><Trash2 size={16} /></button>
                      </>
                    )}
                  </div>
                )}
              </div>
              {isOpen && (
                <div className="px-5 pb-3.5 pl-14" data-testid={`cat-products-${c.id}`}>
                  {items.length === 0 ? (
                    <p className="text-sm text-zinc-400 italic py-1" data-testid={`cat-noproducts-${c.id}`}>No products</p>
                  ) : (
                    <ul className="divide-y divide-zinc-100 rounded-lg border border-zinc-100 bg-zinc-50/40 overflow-hidden">
                      {items.map((p) => (
                        <li key={p.id} data-testid={`cat-product-${p.id}`} className="flex items-center gap-3 px-3 py-2">
                          <span className="h-7 w-7 rounded-md bg-white border border-zinc-200 text-zinc-400 flex items-center justify-center shrink-0"><Package size={14} /></span>
                          <span className="min-w-0 flex-1">
                            <span className="block text-sm font-medium text-zinc-700 truncate">{p.name}{p.status === "archived" && <span className="ml-2 text-[10px] uppercase tracking-wide text-zinc-400">archived</span>}</span>
                            {(p.part_number || p.sku || p.brand) && (
                              <span className="block text-[11px] text-zinc-400 truncate">
                                {(p.part_number || p.sku) && <span className="font-mono">{p.part_number || p.sku}</span>}
                                {(p.part_number || p.sku) && p.brand ? " · " : ""}
                                {p.brand}
                              </span>
                            )}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {confirmAll && (
        <div className="fixed inset-0 z-[70] bg-black/50 flex items-center justify-center p-4" onClick={() => !deletingAll && setConfirmAll(false)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl w-full max-w-md p-6" data-testid="delete-all-confirm-modal">
            <div className="flex items-start gap-3">
              <span className="h-11 w-11 rounded-full bg-red-50 text-red-600 flex items-center justify-center shrink-0"><AlertTriangle size={22} /></span>
              <div className="min-w-0">
                <h3 className="font-heading font-bold text-lg">Delete all categories?</h3>
                <p className="text-sm text-zinc-500 mt-1">This will permanently remove all categories. Products will not be deleted.</p>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button onClick={() => setConfirmAll(false)} disabled={deletingAll} data-testid="delete-all-cancel" className="h-11 px-5 rounded-xl border border-zinc-200 font-semibold text-sm active:scale-95 disabled:opacity-50">Cancel</button>
              <button onClick={deleteAll} disabled={deletingAll} data-testid="delete-all-confirm" className="h-11 px-5 rounded-xl bg-red-600 text-white font-semibold text-sm flex items-center gap-2 active:scale-95 hover:brightness-110 disabled:opacity-50"><Trash2 size={16} />{deletingAll ? "Deleting…" : "Delete All Categories"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
